/** Fault report list with URL-backed filters, sorting, pagination and a create dialog. */
import { useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError, errorMessage } from "@/api/problem";
import { api } from "@/api/client";
import { unwrap } from "@/api/problem";
import { useAuth } from "@/app/auth";
import { AssetReference } from "@/features/entities/AssetReference";
import { PersonReference } from "@/features/entities/PersonReference";
import { AssetPicker } from "@/features/assets/AssetPicker";
import { useAsset } from "@/features/assets/queries";
import { StagedAttachments, useStagedAttachments } from "@/features/documents/StagedAttachments";
import { FormDialog } from "@/shared/components/form-dialog";
import { EntityPicker } from "@/shared/components/pickers";
import {
  REQUEST_CATEGORIES,
  REQUEST_SEVERITIES,
  REQUEST_STATUSES,
  type RequestFilters,
  type ServiceRequest,
  useCreateRequest,
  useRequestPage,
} from "@/features/requests/queries";
import { useSites, useLocationsOf } from "@/features/admin/reference/queries";
import { DataTable } from "@/shared/components/data-table";
import { downloadTableExport, TableExportButton } from "@/shared/components/table-export-button";
import { TableToolbar, type TableFilterDefinition } from "@/shared/components/table-toolbar";
import { bindTableFilters, tableSortFields } from "@/shared/lib/table-controls";
import { ErrorState, LoadingState, PageHeader, StatusBadge } from "@/shared/components/page";
import { formatDateTime } from "@/shared/lib/format";
import { PAGE_SIZES, useListState } from "@/shared/lib/list-state";
import { Button } from "@/shared/ui/button";
import { Checkbox, Field, Input, Select, Textarea } from "@/shared/ui/form";

/** Dialog to submit a fault report; optionally attaches the browser's geolocation. */
export function NewRequestDialog({
  open,
  onOpenChange,
  defaultAssetId,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  defaultAssetId?: string;
}): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const create = useCreateRequest();
  const [assetId, setAssetId] = useState(defaultAssetId ?? "");
  const [submissionId] = useState(() => crypto.randomUUID());
  const [vesselId, setVesselId] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState<string>("fault");
  const [severity, setSeverity] = useState<string>("medium");
  const [outOfService, setOutOfService] = useState(false);
  const [discipline, setDiscipline] = useState("general");
  const [scope, setScope] = useState("component");
  const [siteId, setSiteId] = useState("");
  const [locationId, setLocationId] = useState("");
  const [createdId, setCreatedId] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState("");
  const uploader = useStagedAttachments();
  const asset = useAsset(assetId);
  const sites = useSites();
  const locations = useLocationsOf(siteId || asset.data?.site?.id);
  const fieldErrors = create.error instanceof ApiError ? create.error.fieldErrors() : {};

  const submit = async (): Promise<void> => {
    try {
      const id =
        createdId ??
        (
          await create.mutateAsync({
            title,
            client_submission_id: submissionId,
            description,
            category,
            severity,
            asset_id: assetId || null,
            asset_out_of_service: outOfService,
            reporter_channel: "web",
            discipline,
            restriction_scope: scope,
            site_id: siteId || null,
            location_id: locationId || null,
          })
        ).id;
      setCreatedId(id);
      await uploader.linkTo("request", id);
      onOpenChange(false);
      await navigate({ to: "/requests/$requestId", params: { requestId: id } });
    } catch (error) {
      setUploadError(errorMessage(error));
      throw error;
    }
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("requests.new")}
      onSubmit={submit}
      dirty={
        uploader.hasFiles ||
        Boolean(
          title ||
          description ||
          assetId !== (defaultAssetId ?? "") ||
          vesselId ||
          siteId ||
          locationId ||
          category !== "fault" ||
          severity !== "medium" ||
          discipline !== "general" ||
          scope !== "component" ||
          outOfService,
        )
      }
      onDiscard={uploader.cancelAll}
      submitting={create.isPending || uploader.busy}
      disabled={!title || !uploader.ready}
      error={uploadError || undefined}
      submitLabel={createdId ? t("requests.retryPhotos") : t("app.submit")}
    >
      <Field id="sr-title" label={t("app.title")} error={fieldErrors.title}>
        <Input id="sr-title" value={title} onChange={(e) => setTitle(e.target.value)} required />
      </Field>
      <Field id="sr-asset" label={`${t("app.asset")} (${t("app.optional")})`}>
        <AssetPicker id="sr-asset" value={assetId} onChange={setAssetId} required={false} />
      </Field>
      <Field id="sr-vessel" label={t("requests.vesselContext")}>
        <EntityPicker
          id="sr-vessel"
          value={vesselId}
          searchable
          onChange={(value) => {
            setVesselId(value ?? "");
            setAssetId(value ?? "");
          }}
          queryKey={["requests", "vessel-picker"]}
          load={async (q) =>
            unwrap(
              await api.GET("/api/v1/assets/", {
                params: { query: { vessels_only: true, q, limit: 50 } },
              }),
            ).items.map((row) => ({ id: row.id, label: `${row.code} · ${row.name}` }))
          }
        />
      </Field>
      {vesselId ? (
        <Field id="sr-component" label={t("requests.component")}>
          <EntityPicker
            id="sr-component"
            value={assetId === vesselId ? "" : assetId}
            onChange={(value) => setAssetId(value ?? vesselId)}
            queryKey={["requests", "components", vesselId]}
            load={async () =>
              unwrap(
                await api.GET("/api/v1/assets/", {
                  params: { query: { parent_id: vesselId, limit: 100 } },
                }),
              ).items.map((row) => ({ id: row.id, label: `${row.code} · ${row.name}` }))
            }
          />
        </Field>
      ) : null}
      <Field id="sr-description" label={t("app.description")}>
        <Textarea
          id="sr-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Field>
      {asset.data?.parent_id ? (
        <p className="text-sm">
          {t("requests.componentContext")} · {asset.data.name}
        </p>
      ) : null}
      <Field id="sr-site" label={t("app.site")}>
        <Select
          id="sr-site"
          value={siteId || (asset.data?.site?.id ?? "")}
          onChange={(event) => {
            setSiteId(event.target.value);
            setLocationId("");
          }}
        >
          <option value="">—</option>
          {sites.data?.map((site) => (
            <option key={site.id} value={site.id}>
              {site.name}
            </option>
          ))}
        </Select>
      </Field>
      <Field id="sr-location" label={t("requests.location")}>
        <Select
          id="sr-location"
          value={locationId || (asset.data?.location?.id ?? "")}
          onChange={(event) => setLocationId(event.target.value)}
        >
          <option value="">—</option>
          {locations.data?.map((location) => (
            <option key={location.id} value={location.id}>
              {location.name}
            </option>
          ))}
        </Select>
      </Field>
      <Field id="sr-discipline" label={t("requests.disciplineLabel")}>
        <Select
          id="sr-discipline"
          value={discipline}
          onChange={(event) => setDiscipline(event.target.value)}
        >
          {["general", "mechanical", "electrical", "structural", "safety"].map((value) => (
            <option key={value} value={value}>
              {t(`requests.disciplines.${value}`)}
            </option>
          ))}
        </Select>
      </Field>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field id="sr-category" label={t("app.type")}>
          <Select id="sr-category" value={category} onChange={(e) => setCategory(e.target.value)}>
            {REQUEST_CATEGORIES.map((v) => (
              <option key={v} value={v}>
                {t(`requests.category.${v}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field id="sr-severity" label={t("app.severity")}>
          <Select id="sr-severity" value={severity} onChange={(e) => setSeverity(e.target.value)}>
            {REQUEST_SEVERITIES.map((v) => (
              <option key={v} value={v}>
                {t(`requests.severity.${v}`)}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      <Checkbox
        id="sr-oos"
        label={t("workOrders.assetOutOfService")}
        checked={outOfService}
        onChange={(e) => setOutOfService(e.target.checked)}
      />
      {outOfService ? (
        <Field id="sr-scope" label={t("workOrders.restrictionScope")}>
          <Select id="sr-scope" value={scope} onChange={(event) => setScope(event.target.value)}>
            {["component", "vessel", "both"].map((value) => (
              <option key={value} value={value}>
                {t(`workOrders.restriction.${value}`)}
              </option>
            ))}
          </Select>
        </Field>
      ) : null}
      <StagedAttachments uploader={uploader} />
    </FormDialog>
  );
}

/** Fault report list page. */
export function RequestsPage(): ReactElement {
  const { t } = useTranslation();
  const { can } = useAuth();
  const list = useListState("/requests", "-created_at");
  const { search, state } = list;
  const [createOpen, setCreateOpen] = useState(Boolean(search.asset_id));
  const openOnly = search.status ? false : (search.open_only ?? true);
  const filters: RequestFilters = {
    q: search.q,
    status: search.status,
    open_only: openOnly,
    severity: search.severity,
    category: search.type,
    site_id: search.site_id,
    mine: search.mine,
  };
  const page = useRequestPage(filters, state);
  const sites = useSites();
  const update = list.update;

  const field = bindTableFilters(filters, (patch) =>
    update({
      ...patch,
      ...("category" in patch ? { type: patch.category } : {}),
      ...("status" in patch ? { open_only: false } : {}),
      ...(patch.open_only === true ? { status: undefined } : {}),
    }),
  );
  const definitions: TableFilterDefinition[] = [
    {
      ...field("status", t("app.status")),
      kind: "multi",
      prominent: true,
      options: REQUEST_STATUSES.map((value) => ({ value, label: t(`requests.status.${value}`) })),
    },
    {
      ...field("severity", t("app.severity")),
      kind: "multi",
      prominent: true,
      options: REQUEST_SEVERITIES.map((value) => ({
        value,
        label: t(`requests.severity.${value}`),
      })),
    },
    {
      ...field("category", t("app.type")),
      kind: "select",
      options: REQUEST_CATEGORIES.map((value) => ({
        value,
        label: t(`requests.category.${value}`),
      })),
    },
    {
      ...field("site_id", t("app.site")),
      kind: "select",
      options: (sites.data ?? []).map((site) => ({ value: site.id, label: site.name })),
    },
    {
      ...field("open_only", t("requests.openOnly")),
      kind: "boolean",
      active: openOnly,
      onRemove: () => update({ open_only: false }),
    },
    {
      ...field("mine", t("requests.mine")),
      kind: "boolean",
      active: !!filters.mine,
      prominent: true,
    },
  ];

  const columns: ColumnDef<ServiceRequest>[] = [
    {
      id: "code",
      enableSorting: true,
      header: t("app.code"),
      cell: (c) => <span className="font-mono text-xs">{c.row.original.code}</span>,
    },
    {
      id: "title",
      enableSorting: true,
      header: t("app.title"),
      cell: (c) => <span className="font-medium">{c.row.original.title}</span>,
    },
    {
      id: "asset",
      enableSorting: true,
      header: t("app.asset"),
      cell: (c) => (
        <AssetReference id={c.row.original.asset_id} name={c.row.original.asset_name ?? "—"} />
      ),
    },
    {
      id: "category",
      enableSorting: true,
      header: t("app.type"),
      cell: (c) => t(`requests.category.${c.row.original.category}`),
    },
    {
      id: "severity",
      enableSorting: true,
      header: t("app.severity"),
      cell: (c) => <StatusBadge value={c.row.original.severity} namespace="requests.severity" />,
    },
    {
      id: "status",
      enableSorting: true,
      header: t("app.status"),
      cell: (c) => <StatusBadge value={c.row.original.status} namespace="requests.status" />,
    },
    {
      id: "reporter",
      header: t("requests.reporter"),
      cell: (c) => (
        <PersonReference
          id={c.row.original.reporter_id}
          name={c.row.original.reporter_name ?? "—"}
        />
      ),
    },
    {
      id: "created_at",
      enableSorting: true,
      header: t("app.created"),
      cell: (c) => formatDateTime(c.row.original.created_at),
    },
  ];

  return (
    <div>
      <PageHeader
        title={t("requests.title")}
        actions={
          can("requests.request.create") ? (
            <Button onClick={() => setCreateOpen(true)}>{t("requests.new")}</Button>
          ) : null
        }
      />
      {createOpen && can("requests.request.create") ? (
        <NewRequestDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          defaultAssetId={search.asset_id}
        />
      ) : null}
      <TableToolbar
        filters={definitions}
        search={search.q}
        searchPlaceholder={t("tablePilot.requestSearch")}
        searchDescription={t("tablePilot.requestSearchScope")}
        onSearchChange={(q) => update({ q })}
        sorting={state.sort}
        sortFields={tableSortFields(columns)}
        onSortingChange={list.setSort}
        onReset={() =>
          update({
            q: undefined,
            status: undefined,
            severity: undefined,
            type: undefined,
            site_id: undefined,
            mine: undefined,
            open_only: undefined,
          })
        }
        actions={
          <TableExportButton
            onExport={(format) =>
              downloadTableExport(
                "/api/v1/requests/",
                "fault-reports",
                { ...filters, ordering: state.sort },
                format,
              )
            }
          />
        }
      />
      {page.query.isPending ? (
        <LoadingState />
      ) : page.query.isError ? (
        <ErrorState error={page.query.error} onRetry={() => void page.query.refetch()} />
      ) : (
        <DataTable
          columns={columns}
          minWidth={1000}
          data={page.items}
          getRowId={(r) => r.id}
          rowHref={(r) => `/requests/${r.id}`}
          sorting={state.sort}
          onSortingChange={list.setSort}
          isFetching={page.query.isFetching}
          pagination={{
            page: page.page,
            pages: page.pages,
            total: page.total,
            pageSize: page.pageSize,
            onPageChange: list.setPage,
            onPageSizeChange: list.setSize,
            sizes: PAGE_SIZES,
            extendedNavigation: true,
          }}
        />
      )}
    </div>
  );
}
